import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Tag, CreditCard } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useCart } from "../context/CartContext";
import { eur, waLink } from "../config";
import { checkPromo, createOrder, createPaymentCheckout, getPaypalConfig, createPaypalOrder, capturePaypalOrder, getCheckoutQuote } from "../lib/api";
import { WhatsAppIcon } from "../components/WhatsAppButton";

const REQUIRED = ["first_name", "last_name", "email", "phone", "address", "commune", "postal_code"];

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", commune: "", postal_code: "", note: "", payment_method: "card" });
  const [promo, setPromo] = useState({ code: "", applied: null });
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    if (params.get("canceled")) toast.info("Paiement annulé. Votre panier est conservé.");
  }, [params]);

  useEffect(() => {
    getPaypalConfig().then(setPaypalCfg).catch(() => setPaypalCfg({ configured: false }));
  }, []);

  // Estimation serveur (livraison au poids + remise + total)
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    const payload = items.map((i) => ({ slug: i.slug, quantity: i.quantity, size: i.size, color: i.color }));
    getCheckoutQuote(payload, promo.applied?.code || null)
      .then((q) => { if (!cancelled) setQuote(q); })
      .catch(() => { if (!cancelled) setQuote(null); })
      .finally(() => { if (!cancelled) setQuoting(false); });
    return () => { cancelled = true; };
  }, [items, promo.applied?.code]);

  const validateForm = () => {
    const missing = REQUIRED.filter((k) => !form[k].trim());
    if (missing.length) { toast.error("Veuillez remplir toutes vos informations avant de payer."); return false; }
    return true;
  };

  const cartPayload = () => ({
    promo_code: promo.applied?.code || null,
    items: items.map((i) => ({ slug: i.slug, quantity: i.quantity, size: i.size, color: i.color })),
    first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
    address: form.address, commune: form.commune, postal_code: form.postal_code, note: form.note,
  });

  const discount = quote ? quote.discount : (promo.applied ? promo.applied.discount : 0);
  const shipping = quote ? quote.shipping : 0;
  const total = quote ? quote.total : Math.max(0, subtotal - discount + shipping);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyPromo = async () => {
    if (!promo.code.trim()) return;
    try {
      const r = await checkPromo(promo.code, subtotal);
      setPromo({ ...promo, applied: r });
      toast.success(`Code ${r.code} appliqué (-${r.value}%)`);
    } catch { toast.error("Code promo invalide"); setPromo({ ...promo, applied: null }); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) { toast.error("Votre panier est vide"); return; }
    setPlacing(true);
    try {
      if (form.payment_method === "card") {
        const r = await createPaymentCheckout({ origin_url: window.location.origin, ...cartPayload() });
        window.location.href = r.checkout_url;
        return;
      }
      const payload = {
        ...form, promo_code: promo.applied?.code || null,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, size: i.size, color: i.color, image: i.image })),
        subtotal, discount, shipping, total,
      };
      const r = await createOrder(payload);
      setDone(r.order_number);
      clearCart();
    } catch (err) { console.error(err); toast.error("Erreur lors de la commande"); }
    setPlacing(false);
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="order-confirmation">
        <div className="w-16 h-16 rounded-full bg-dore/20 flex items-center justify-center mx-auto mb-6"><Check size={32} className="text-dore" /></div>
        <h1 className="font-display text-3xl text-encre">Merci pour votre commande !</h1>
        <p className="text-ardoise mt-3">Votre commande <b className="text-encre" data-testid="order-number">{done}</b> a bien été enregistrée. Nous vous contacterons rapidement pour confirmer la livraison.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <button onClick={() => navigate("/boutique")} className="bg-noir text-white px-8 py-3.5 text-xs uppercase tracking-widest hover:bg-ardoise transition-colors">Continuer mes achats</button>
          <a href={waLink(`Bonjour ! Je viens de passer la commande ${done}.`)} target="_blank" rel="noreferrer" className="border border-whatsapp text-whatsapp px-8 py-3.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-whatsapp hover:text-white transition-colors"><WhatsAppIcon size={15} /> Confirmer sur WhatsApp</a>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return <div className="max-w-xl mx-auto px-6 py-24 text-center"><p className="text-ardoise mb-6">Votre panier est vide.</p><button onClick={() => navigate("/boutique")} className="bg-noir text-white px-8 py-3.5 text-xs uppercase tracking-widest">Découvrir la boutique</button></div>;
  }

  return (
    <div data-testid="checkout-page">
      <div className="bg-noir text-center py-12 px-6"><h1 className="font-display text-3xl md:text-5xl text-white">Finaliser ma commande</h1></div>
      <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_380px] gap-10">
        <form onSubmit={submit} className="space-y-8" data-testid="checkout-form">
          <fieldset className="space-y-4">
            <legend className="font-display text-xl text-encre mb-2">1. Vos informations</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Prénom" value={form.first_name} onChange={set("first_name")} testid="co-first" required />
              <Input label="Nom" value={form.last_name} onChange={set("last_name")} testid="co-last" required />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Email" type="email" value={form.email} onChange={set("email")} testid="co-email" required />
              <Input label="Téléphone / WhatsApp" value={form.phone} onChange={set("phone")} testid="co-phone" required />
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-display text-xl text-encre mb-2">2. Livraison</legend>
            <Input label="Adresse complète" value={form.address} onChange={set("address")} testid="co-address" required />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Commune (Guadeloupe)" value={form.commune} onChange={set("commune")} testid="co-commune" required />
              <Input label="Code postal" value={form.postal_code} onChange={set("postal_code")} testid="co-postal" required />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ardoise">Note (optionnel)</label>
              <textarea value={form.note} onChange={set("note")} rows={2} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore mt-1" />
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-display text-xl text-encre mb-2">3. Paiement</legend>
            {[{ v: "card", l: "Carte bancaire (paiement sécurisé)", icon: true }, ...(paypalCfg?.configured ? [{ v: "paypal", l: "PayPal", paypal: true }] : []), { v: "cash_on_delivery", l: "Paiement à la livraison" }, { v: "whatsapp", l: "Régler via WhatsApp avec Esther" }].map((o) => (
              <label key={o.v} data-testid={`pay-${o.v}`} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${form.payment_method === o.v ? "border-dore bg-dore/5" : "border-dore/30"}`}>
                <input type="radio" name="pay" checked={form.payment_method === o.v} onChange={() => setForm({ ...form, payment_method: o.v })} className="accent-dore" />
                <span className="text-sm text-encre flex items-center gap-2">{o.icon && <CreditCard size={16} className="text-dore" />}{o.paypal && <span className="font-bold italic text-[#003087]">Pay<span className="text-[#0070E0]">Pal</span></span>}{!o.paypal && o.l}{o.paypal && ""}</span>
              </label>
            ))}
            <p className="text-xs text-ardoise/60">Paiement 100% sécurisé via Stripe — <b className="text-encre">Apple Pay & Google Pay disponibles</b> selon votre appareil. Vous pouvez aussi payer à la livraison ou sur WhatsApp.</p>
          </fieldset>

          {form.payment_method === "paypal" && paypalCfg?.configured ? (
            <div data-testid="paypal-buttons" className="pt-1">
              <PayPalScriptProvider options={{ "client-id": paypalCfg.client_id, currency: "EUR", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                  forceReRender={[total, promo.applied?.code]}
                  createOrder={async () => {
                    if (!validateForm()) throw new Error("form_incomplete");
                    const r = await createPaypalOrder(cartPayload());
                    return r.paypal_order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const r = await capturePaypalOrder(data.orderID);
                      if (r.payment_status === "paid") { setDone(r.order_number); clearCart(); }
                      else toast.error("Paiement PayPal non finalisé.");
                    } catch { toast.error("Erreur lors de la capture du paiement PayPal."); }
                  }}
                  onError={() => toast.error("Une erreur PayPal est survenue. Réessayez.")}
                />
              </PayPalScriptProvider>
            </div>
          ) : (
            <button disabled={placing} className="w-full bg-noir text-white py-4 text-xs uppercase tracking-widest font-semibold hover:bg-ardoise transition-colors disabled:opacity-50" data-testid="place-order-btn">{placing ? "Traitement..." : form.payment_method === "card" ? `Payer par carte · ${eur(total)}` : `Valider ma commande · ${eur(total)}`}</button>
          )}
        </form>

        <aside className="bg-white border border-dore/20 p-6 h-fit lg:sticky lg:top-28">
          <h3 className="font-display text-xl text-encre mb-4">Récapitulatif</h3>
          <div className="space-y-4 max-h-64 overflow-y-auto no-scrollbar">
            {items.map((i) => (
              <div key={i.key} className="flex gap-3">
                <img src={i.image} alt={i.name} className="w-14 h-16 object-cover" />
                <div className="flex-1 text-sm">
                  <p className="text-encre leading-tight">{i.name}</p>
                  <p className="text-ardoise/60 text-xs">{[i.size, i.color].filter(Boolean).join(" · ")} · x{i.quantity}</p>
                </div>
                <span className="text-sm text-encre">{eur(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mt-5">
            <div className="flex-1 flex items-center border border-dore/30 px-3"><Tag size={15} className="text-dore" /><input value={promo.code} onChange={(e) => setPromo({ ...promo, code: e.target.value })} placeholder="Code promo" className="flex-1 px-2 py-2.5 text-sm outline-none" data-testid="promo-input" /></div>
            <button type="button" onClick={applyPromo} className="bg-encre text-white px-4 text-xs uppercase tracking-wider" data-testid="promo-apply">OK</button>
          </div>
          <p className="text-xs text-ardoise/60 mt-2">Essayez : ESTHER15 ou MERCI10</p>

          <div className="border-t border-dore/20 mt-5 pt-4 space-y-2 text-sm">
            <Row label="Sous-total" value={eur(quote ? quote.subtotal : subtotal)} />
            {discount > 0 && <Row label={`Réduction (${promo.applied?.code || quote?.promo_code || ""})`} value={`-${eur(discount)}`} accent />}
            <Row label="Livraison (Guadeloupe)" value={quoting && !quote ? "…" : shipping === 0 ? "—" : eur(shipping)} />
            <p className="text-[11px] text-ardoise/60" data-testid="shipping-note">Frais de livraison calculés selon le poids de votre commande.</p>
            <div className="flex justify-between pt-2 border-t border-dore/20"><span className="font-medium text-encre">Total</span><span className="font-display text-xl text-dore" data-testid="checkout-total">{eur(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Input = ({ label, testid, ...props }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-ardoise">{label}</label>
    <input {...props} data-testid={testid} className="w-full border border-dore/30 px-4 py-3 text-sm outline-none focus:border-dore mt-1" />
  </div>
);
const Row = ({ label, value, accent }) => (
  <div className="flex justify-between"><span className="text-ardoise">{label}</span><span className={accent ? "text-dore" : "text-encre"}>{value}</span></div>
);
